import {
  Alert, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import {
  CST_ICMS_LR,
  CST_ICMS_SN,
  getAliquotaInterestadual,
  IPI_CST,
  MOD_BC_ICMS,
  MOD_BC_ICMS_ST,
  normalizarCsosnValor,
  ORIGEM_MERCADORIA,
  PIS_COFINS_CST,
} from '../utils/fiscalConstants';
import { formatCurrencyInputBR } from '../utils/decimalBr';

const numField = (label, key, item, onChange, helperText) => (
  <TextField
    label={label}
    value={item[key] ?? ''}
    onChange={(e) => onChange(key, e.target.value)}
    fullWidth
    helperText={helperText}
  />
);

const moedaField = (label, key, item, onChange, helperText) => (
  <TextField
    label={label}
    value={item[key] ?? ''}
    onChange={(e) => onChange(key, formatCurrencyInputBR(e.target.value))}
    fullWidth
    inputMode="numeric"
    helperText={helperText}
  />
);

/**
 * Seção de tributos do item NF-e (paridade aba Tributos NF do desktop).
 */
export default function NfeItemTributosSection({
  item,
  onChange,
  idDest,
  ufEmitente,
  ufDestinatario,
  simplesNacional = true,
  isDevolucao = false,
}) {
  const set = (key, value) => onChange({ ...item, [key]: value });
  const idDestNum = Number(idDest);
  const aliqInterestadual = idDestNum === 2
    ? getAliquotaInterestadual(ufEmitente, ufDestinatario)
    : null;
  const icmsOptions = simplesNacional ? CST_ICMS_SN : CST_ICMS_LR;
  const icmsLabel = simplesNacional ? 'CSOSN' : 'CST ICMS';
  const icmsSectionTitle = simplesNacional ? 'ICMS — Simples Nacional' : 'ICMS — Regime normal (Lucro Real/Presumido)';

  const onIcmsCodeChange = (e) => {
    const n = Number(e.target.value);
    const padded = simplesNacional ? String(n).padStart(3, '0') : String(n).padStart(2, '0');
    set('csosn', padded);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" color="text.secondary">{icmsSectionTitle}</Typography>
      {isDevolucao && !simplesNacional && (
        <Alert severity="info">
          Em devolução para Lucro Real, informe CST, modalidade de BC (ex.: pauta por valor)
          e valores de ICMS/PIS/COFINS conforme a NF de origem — cada imposto é declarado
          separadamente na nota.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="item-csosn-label">{icmsLabel}</InputLabel>
          <Select
            labelId="item-csosn-label"
            label={icmsLabel}
            value={normalizarCsosnValor(item.csosn)}
            onChange={onIcmsCodeChange}
          >
            {icmsOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="item-origem-label">Origem da mercadoria</InputLabel>
          <Select
            labelId="item-origem-label"
            label="Origem da mercadoria"
            value={Number(item.origem) || 0}
            onChange={(e) => set('origem', Number(e.target.value))}
          >
            {ORIGEM_MERCADORIA.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="item-modbc-label">Modalidade da base de cálculo ICMS</InputLabel>
          <Select
            labelId="item-modbc-label"
            label="Modalidade da base de cálculo ICMS"
            value={item.modBc === '' || item.modBc == null ? 3 : Number(item.modBc)}
            onChange={(e) => set('modBc', Number(e.target.value))}
          >
            {MOD_BC_ICMS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {numField('% redução da base de cálculo', 'pRedBc', item, set)}
        {moedaField('Base de cálculo ICMS', 'vBc', item, set)}
        {numField('% alíquota ICMS', 'pIcms', item, set)}
        {moedaField('Valor ICMS', 'vIcms', item, set)}
      </Stack>
      {simplesNacional && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {numField('% crédito SN', 'pCredSn', item, set)}
          {moedaField('Crédito ICMS aproveitado', 'vCredIcmsSn', item, set)}
        </Stack>
      )}

      <Typography variant="subtitle2" color="text.secondary">ICMS substituição tributária</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="item-modbcst-label">Modalidade BC ICMS ST</InputLabel>
          <Select
            labelId="item-modbcst-label"
            label="Modalidade BC ICMS ST"
            value={item.modBcSt === '' || item.modBcSt == null ? '' : Number(item.modBcSt)}
            onChange={(e) => set('modBcSt', e.target.value === '' ? '' : Number(e.target.value))}
            displayEmpty
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {MOD_BC_ICMS_ST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {numField('% MVA ST', 'pMvaSt', item, set)}
        {numField('% redução BC ST', 'pRedBcSt', item, set)}
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {moedaField('Base de cálculo ICMS ST', 'vBcSt', item, set)}
        {numField('% alíquota ST', 'pIcmsSt', item, set)}
        {moedaField('Valor ICMS ST', 'vIcmsSt', item, set)}
      </Stack>

      <Typography variant="subtitle2" color="text.secondary">Benefício fiscal / FCP</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="cBenef"
          value={item.cBenef ?? ''}
          onChange={(e) => set('cBenef', e.target.value.toUpperCase())}
          fullWidth
          helperText="8/10 caracteres ou SEM CBENEF"
        />
        {numField('% FCP', 'pFcp', item, set)}
        {moedaField('Valor FCP', 'vFcp', item, set)}
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {numField('% FCP ST', 'pFcpSt', item, set)}
        {moedaField('Valor FCP ST', 'vFcpSt', item, set)}
      </Stack>

      {idDestNum === 2 && (
        <>
          <Typography variant="subtitle2" color="text.secondary">DIFAL — partilha interestadual</Typography>
          {aliqInterestadual != null && (
            <Alert severity="info">
              Operação {ufEmitente || '?'} → {ufDestinatario || '?'}:
              alíquota interestadual sugerida: <strong>{aliqInterestadual}%</strong>
            </Alert>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {moedaField('BC ICMS na UF destino', 'vBcUfDest', item, set)}
            {numField('% FCP na UF destino', 'pFcpUfDest', item, set)}
            {numField('% ICMS na UF destino', 'pIcmsUfDest', item, set)}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {numField('% ICMS interestadual', 'pIcmsInter', item, set, aliqInterestadual != null ? `Sugerido: ${aliqInterestadual}%` : undefined)}
            {numField('% partilha UF destino', 'pIcmsInterPart', item, set, 'Padrão 100% para consumidor final')}
            {moedaField('Valor FCP UF destino', 'vFcpUfDest', item, set)}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {moedaField('Valor ICMS UF destino', 'vIcmsUfDest', item, set)}
            {moedaField('Valor ICMS UF remetente', 'vIcmsUfRemet', item, set)}
          </Stack>
        </>
      )}

      {idDestNum === 3 && (
        <Alert severity="warning">
          Operação com exterior: confira origem da mercadoria, CFOP de exportação e dados do
          destinatário estrangeiro no cadastro do cliente.
        </Alert>
      )}

      <Typography variant="subtitle2" color="text.secondary">IPI</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="item-ipi-label">CST IPI</InputLabel>
          <Select
            labelId="item-ipi-label"
            label="CST IPI"
            value={Number(item.cstIpi) || 99}
            onChange={(e) => set('cstIpi', Number(e.target.value))}
          >
            {IPI_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {moedaField('BC IPI', 'vBcIpi', item, set)}
        {numField('% alíquota IPI', 'pIpi', item, set)}
        {moedaField('Valor IPI', 'vIpi', item, set)}
      </Stack>

      <Typography variant="subtitle2" color="text.secondary">PIS</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="item-pis-label">CST PIS</InputLabel>
          <Select
            labelId="item-pis-label"
            label="CST PIS"
            value={Number(item.cstPis) || 99}
            onChange={(e) => set('cstPis', Number(e.target.value))}
          >
            {PIS_COFINS_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {moedaField('BC PIS', 'vBcPis', item, set)}
        {numField('% alíquota PIS', 'pPis', item, set)}
        {moedaField('Valor PIS', 'vPis', item, set)}
      </Stack>

      <Typography variant="subtitle2" color="text.secondary">COFINS</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="item-cofins-label">CST COFINS</InputLabel>
          <Select
            labelId="item-cofins-label"
            label="CST COFINS"
            value={Number(item.cstCofins) || 99}
            onChange={(e) => set('cstCofins', Number(e.target.value))}
          >
            {PIS_COFINS_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {moedaField('BC COFINS', 'vBcCofins', item, set)}
        {numField('% alíquota COFINS', 'pCofins', item, set)}
        {moedaField('Valor COFINS', 'vCofins', item, set)}
      </Stack>
    </Stack>
  );
}
